import clsx from 'clsx';
import { useBetslipStore } from '@/store/betslip';
import { useSiteStore } from '@/store/site';
import { BetTicket } from '../bet-ticket';
import { ParlayArea } from '../parlay-area';
import { BetMsgModel, BetslipTab, BETTING_CODE, CanBetResultModel, ParlayTicketsModel, TicketModel, useTicketBet } from '@/hooks/query';
import { StakePerBet } from '../stake-per-bet';
import { Payout } from '../payout';
import { KafkaNote } from '@nf/utils-common/constants';
import { addCommasWithFix } from '@/utils/odds-utils';
import { setKafkaNormal } from '@/hooks/query/use-kafka-log';
import { Icon, TrashCanIcon } from '@/components/icons/svg-icons';
import { Notice } from '../bet-msg';
import { useTranslations } from 'next-intl';
import { useBetslipContext } from '@/features/betslip/contexts/betslip-context';
import { betslipStyle } from '@/features/betslip/betslip.style';
import { ViewEnum } from '@/types';
import { useAppearanceStore } from '@/store/appearance';
import { BetSetting } from '../bet-setting';
import { BetslipService } from '@/services/betslip.service';
import { AnalyseKey, useConfigsAnalyseAttributeWithToggle } from '@/features/next-analyse';
import { useAsideBetslip } from '@/features/betslip/hook/use-aside-betslip';
import { Button } from '@nf/ui';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ParlayTabSwitch } from '../parlay-tab-switch';
import { useFloatBetslip } from '@/features/betslip/hook/use-float-betslip';
import { SabaCoinsToggle } from '@/features/saba-coins/saba-coins-toggle';
import { useGetSiteConfigs } from '@/hooks/query/use-site-configs';

interface MultiBetCollapseProps {
	currentMultiTickets: TicketModel[];
	currentParlayTicketModel: ParlayTicketsModel | null;
	canBetResult: CanBetResultModel;
	isIndependentMode: boolean;
	betslipTab: BetslipTab;
}

interface MultiBetExpandProps {
	hasParlay: boolean;
	currentMultiTickets: TicketModel[];
	currentParlayTicketModel: ParlayTicketsModel | null;
	getTotalBet: (isIndependentMode: boolean, isConfirmBet?: boolean) => number;
	getTotalPayout: (isIndependentMode: boolean) => number;
	getBettingAmount: () => string;
	canBetResult: CanBetResultModel;
}

export const MultiBet = () => {
	const { currencyName, currencySymbol, isIndependentMode } = useBetslipContext();
	const [
		isExpand,
		currentMultiTickets,
		currentParlayTicketModel,
		canBetResult,
		setIsExpand,
		getTotalBet,
		getTotalStake,
		getTotalPayout,
		betslipTab,
		useSabaCoin
	] = useBetslipStore(state => [
		state.isExpand,
		state.currentMultiTickets,
		state.currentParlayTicketModel,
		state.canBetResult,
		state.setIsExpand,
		state.getTotalBet,
		state.getTotalStake,
		state.getTotalPayout,
		state.betslipTab,
		state.useSabaCoin,
	]);
	const { SiteConfigsData } = useGetSiteConfigs();
	const { activeAsideBetSlip } = useAsideBetslip();
	const { activeFloatBetSlip } = useFloatBetslip();
	const hasParlay = (currentParlayTicketModel && currentParlayTicketModel.comboData.length > 0) ? true : false;
	let showParlayQuickBet = false;

	if (hasParlay) {
		const multiHasStake = currentMultiTickets.filter(ticket => ticket.stake);
		const parlayHasStake = currentParlayTicketModel?.comboData.filter(ticket => ticket.stake);

		showParlayQuickBet = (multiHasStake.length === 0 && parlayHasStake?.length === 0)	// 全不選
			|| (hasParlay && multiHasStake.length === 0 && parlayHasStake?.length === 1 && parlayHasStake[0].isMixParlay === true);	// 只選 mix parlay
	}

	// 獨立下注模式不使用收合parlay模式
	if (showParlayQuickBet) {
		showParlayQuickBet = !isIndependentMode;
	}

	const getBettingAmount = () => {
		const totalStake = addCommasWithFix(getTotalStake(isIndependentMode));
		return `${useSabaCoin? (SiteConfigsData?.EnableCurrencyDisplay ? 'SBC' : '') : currencyName} ${currencySymbol}${totalStake}`;
	}

	//muti bet 第一次渲染時需要收合, float模式則不改變狀態
	const [isCheckExpand, setIsCheckExpand] = useState(false);
	useEffect(() => {
		if (!activeFloatBetSlip && isExpand && !isCheckExpand) {
			setIsExpand(false);
			setIsCheckExpand(true);
		}
	}, []);

	return (
		<div className={clsx('bet-slip__container', { [betslipStyle.betslipContainer]: activeAsideBetSlip })}>
			{isExpand || activeAsideBetSlip ?
				<MultiBetExpand
					hasParlay={hasParlay}
					currentMultiTickets={currentMultiTickets}
					currentParlayTicketModel={currentParlayTicketModel}
					getTotalBet={getTotalBet}
					getTotalPayout={getTotalPayout}
					getBettingAmount={getBettingAmount}
					canBetResult={canBetResult}
				/> :
				<MultiBetCollapse
					currentMultiTickets={currentMultiTickets}
					currentParlayTicketModel={currentParlayTicketModel}
					canBetResult={canBetResult}
					isIndependentMode={isIndependentMode}
					betslipTab={betslipTab}
				/>
			}
		</div>
	)
};

/** Multi Bet - 收合 */
export const MultiBetCollapse = ({
	currentMultiTickets,
	currentParlayTicketModel,
	canBetResult,
	isIndependentMode,
	betslipTab
}: MultiBetCollapseProps) => {
	const isFirstRender = useRef(true);
	const [parlayNotice, setParlayNotice] = useState('');
	const canBet = canBetResult.canBet;

	const getNotices = () => {
		let msgArray = [...canBetResult.innerMsg, ...canBetResult.outerMsg];

		msgArray = msgArray.filter(msg => msg.code === BETTING_CODE.ExceedLimitSelection); // multi 收合模式僅顯示超過20場提示

		const parlayMaxPayoutCodeArray = ([BETTING_CODE.ParlayMaxBetLessThanMinBet, BETTING_CODE.ParlayNoComboAvailable] as Array<number>);
		const parlayMinOddsCodeArray = ([BETTING_CODE.ParlayMinOdds, BETTING_CODE.ParlayComboByPool] as Array<number>);
		const firstParlayMaxPayoutTicketKey = msgArray.find(msg => parlayMaxPayoutCodeArray.includes(msg.code))?.ticketKey;
		const BettingCodeCond = (msg: BetMsgModel) => ![...parlayMaxPayoutCodeArray, ...parlayMinOddsCodeArray].includes(msg.code);
		const parlayMaxPayoutCond = (msg: BetMsgModel) => parlayMaxPayoutCodeArray.includes(msg.code) && (msg.ticketKey === '' || (msg.ticketKey !== '' && msg.ticketKey === firstParlayMaxPayoutTicketKey));

		const notices = msgArray
			.filter(msg => BettingCodeCond(msg) || parlayMaxPayoutCond(msg))
			.map((value, idx) => (
				<Notice
					key={idx}
					code={value.code}
					msg={value.msg}
					msgResourceKey={value.msgResourceKey}
				/>
			));

		return notices;
	}

	const getBetChoiceSummary = () => {
		const multiTickets = currentMultiTickets;
		const betChoiceArray = multiTickets.map(ticket => ticket.ChoiceValue);
		const summary = betChoiceArray.join(', ');

		return summary;
	}

	useEffect(() => {
		if (isFirstRender.current) {
			isFirstRender.current = false;

			return;
		}

		const mixParlay = currentParlayTicketModel?.comboData?.find(parlay => parlay.isMixParlay && parlay.CanBet);

		if (mixParlay) {
			setParlayNotice(`${mixParlay?.Name} @${mixParlay?.Odds}`);

			setTimeout(() => {
				setParlayNotice('');
			}, 3000);
		}
	}, [currentParlayTicketModel?.comboData]);

	return (
		<>
			{
				// betslip完整版新增需求, 再縮小情況下除分離投注single tab, 只要串成parlay都需三秒提示
				(!isIndependentMode || betslipTab === 'parlay') && parlayNotice ?
					<div className={clsx('bet-slip__notice', betslipStyle.notice)}>{parlayNotice}</div> : null
			}
			{
				getNotices()
			}
			<div className="bet-slip__collapse">
				<div className="bet-slip__collapse-text" data-error={canBet ? 'false' : 'true'}>
					<div dangerouslySetInnerHTML={{ __html: getBetChoiceSummary() }}></div>
				</div>
			</div>
		</>
	);
}

/** Multi Bet - 展開 */
const MultiBetExpand = ({
	hasParlay,
	currentMultiTickets,
	currentParlayTicketModel,
	getTotalBet,
	getTotalPayout,
	getBettingAmount,
	canBetResult
}: MultiBetExpandProps) => {
	const t = useTranslations('General');
	const { oddsType, balance, isIndependentMode } = useBetslipContext();
	const { refetch, isFetching } = useTicketBet();
	const view = useAppearanceStore(state => state.view);
	const [
		betslipType,
		betslipTab,
		checkStake,
		removeAllTicket,
		useSabaCoin
	] = useBetslipStore(state => [
		state.betslipType,
		state.betslipTab,
		state.checkStake,
		state.removeAllTicket,
		state.useSabaCoin
	]);
	const [isParlayMode] = useSiteStore(state => [state.isParlayMode]);
	const { activeAsideBetSlip } = useAsideBetslip();
	const { activeFloatBetSlip } = useFloatBetslip();
	const isParlayTab = betslipTab === 'parlay';
	const isIndependentParlayLayout = isIndependentMode && isParlayTab;

	const innerMsg = canBetResult.innerMsg;
	const canBet = isIndependentParlayLayout && betslipType === 'multi' ?
		currentParlayTicketModel?.comboData?.some(parlayTicket => parlayTicket.CanBet) :
		canBetResult.canBet;
	const hasParlayBet = useMemo(() => !!(currentParlayTicketModel?.comboData?.some(({ stake }) => stake > 0)), [currentParlayTicketModel?.comboData]);
	const { getRef } = useConfigsAnalyseAttributeWithToggle([{
		key: 'placebet-parlay-bet',
		activeKey: 'placebet-parlay-bet',
		inactiveKey: 'placebet-multi-bet',
		isActive: hasParlayBet,
		analyseKey: AnalyseKey.Refined
	}]);

	const ticketGroupsByMatchId = currentMultiTickets.reduce((array, ticket) => {
		const existingGroup = array.find(group => group.some(groupTicket => groupTicket.Matchid == ticket.Matchid || groupTicket.Matchid == ticket.ParentMatchid));

		if (existingGroup) {
			existingGroup.push(ticket);
		} else {
			array.push([ticket]);
		}

		return array;
	}, new Array<Array<TicketModel>>);

	const canBetCount = innerMsg.reduce((accumulator, betMessageModel) => {
		return !betMessageModel.canBet &&
			currentMultiTickets.find(ticket => ticket.key == betMessageModel.ticketKey)
			? --accumulator :
			accumulator;
	}, currentMultiTickets.length);

	const getNotices = () => {
		if (canBetResult.outerMsg.length === 0) return null;

		const notices = canBetResult.outerMsg
			.filter(msg => msg.code !== BETTING_CODE.ExceedLimitSelection)
			// 獨立模式 single tab不顯示same match notice
			.filter(msg => msg.code !== BETTING_CODE.SameMatch || (!isIndependentMode || isParlayTab))
			.map((value, idx) => (
				<Notice
					key={idx}
					code={value.code}
					msg={value.msg}
					msgResourceKey={value.msgResourceKey}
					status={value.cssStatus}
				/>
			));

		return notices;
	}

	const onClick = () => {
		if (checkStake(isIndependentMode, oddsType) && !isFetching) {
			BetslipService.setMarsBetKafka(view, isParlayMode);
			refetch();
		}

		if (view === ViewEnum.galaxy) {
			setKafkaNormal({
				note: KafkaNote.GalaxyV2,
				type: 8,
				action: hasParlay ? 13 : 12
			});
		}
	};

	const isEndingGameMultiTicket = (currentMultiTickets.filter(ticket => ['647', '648', '649', '650'].includes(ticket.Bettype)).length > 1 &&
		currentMultiTickets.filter(ticket => !['647', '648', '649', '650'].includes(ticket.Bettype)).length > 0) || (
		currentMultiTickets.filter(ticket => ['647', '648', '649', '650'].includes(ticket.Bettype)).length > 1
	);

	const removeAllTicketWithStopPropagation = (event: React.MouseEvent<HTMLElement>) => {
		removeAllTicket();
		event.stopPropagation();
	}

	return (
		<>

			{isIndependentMode ? (
				<div className={clsx('bet-slip__switch', {
					[betslipStyle.floatBetslipSwitch]: activeFloatBetSlip,
					[betslipStyle.asideBetslipSwitch]: activeAsideBetSlip,
				})}
				>
					<ParlayTabSwitch />
				</div>
			) : null}
			<div className={clsx('scrollbar bet-slip-area', {
				'bet-slip__independent-single': isIndependentMode && !activeFloatBetSlip,
				[betslipStyle.singleAreaContainer]: isIndependentMode || activeFloatBetSlip,
				[betslipStyle.singleAreaFloat]: activeFloatBetSlip,
			})}
			>
				{!hasParlay && (isIndependentParlayLayout || (isParlayMode && !isIndependentMode)) ? (
					<div className={clsx(betslipStyle.parlayWarning, {
						[betslipStyle.parlayModeWarning]: activeFloatBetSlip,
						'bet-slip__parlay-fail': isIndependentMode,
						[betslipStyle.independentParlayFail]: isIndependentMode,
					})}
					>
						<Icon name={view === ViewEnum.mars ? 'parlay' : 'ticket-error-line'} />
						<span className={clsx({ 'bet-slip__parlay-fail-text': isIndependentMode })}>{t('lbl_MarsParlayNotEstablished')}</span>
					</div>
				) : null}

				<SabaCoinsToggle />

				<div className={clsx('single-area-wrap', { [betslipStyle.singleAreaWrap]: activeFloatBetSlip })}>

					{
						hasParlay && !activeFloatBetSlip && !isIndependentMode ? (
							<div className={betslipStyle.subTitleWrapper}>
								<div className={betslipStyle.subTitle}>
									{t('lbl_SingleBet')}
								</div>
							</div>
						) : null
					}
					{
						ticketGroupsByMatchId.map((ticketGroup, idx) => (
							ticketGroup.length > 1 && (!isIndependentMode || isParlayTab) ?
								<div key={ticketGroup[0].Matchid} className="bet-slip__same-match">
									{
										ticketGroup.map(ticket => (
											<BetTicket
												key={ticket.key}
												ticket={ticket}
												showBetInput={true}
												canBetResult={innerMsg.find(msg => msg.ticketKey === ticket.key)}
											/>
										))
									}
								</div> :
								ticketGroup.map(ticket => (
									<BetTicket
										key={ticket.key}
										ticket={ticket}
										ticketNumber={idx + 1}
										showBetInput={true}
										canBetResult={innerMsg.find(msg => msg.ticketKey === ticket.key)}
									/>
								))
						))
					}
					{
						canBetCount > 1 && !isIndependentParlayLayout ? <StakePerBet isParlay={false} /> : null
					}
				</div>
				{
					hasParlay && !activeFloatBetSlip && !isIndependentMode ? <ParlayArea canBetResult={canBetResult} /> : null
				}
			</div>
			<div className={clsx({
				'bet-slip__independent-bottom': isIndependentMode || activeAsideBetSlip,
				[betslipStyle.parlayModeBottomFixed]: activeFloatBetSlip,
				[betslipStyle.asideStickyFooter]: activeAsideBetSlip,
			})}
			>
				{
					hasParlay && ((activeFloatBetSlip && !isIndependentMode) || (isIndependentParlayLayout && currentParlayTicketModel?.comboData?.length)) ?
						<ParlayArea canBetResult={canBetResult} /> :
						null
				}
				{
					getNotices()
				}
				{
					canBet && (!isIndependentParlayLayout || (currentParlayTicketModel?.comboData?.length)) ? (
						<div className={clsx({ [betslipStyle.parlayModeFooter]: activeFloatBetSlip })}>
							<div className="bet-slip__betting">
								{activeAsideBetSlip &&
									<Button className={betslipStyle.asideTrashCanIcon} variant="icon" onClick={removeAllTicketWithStopPropagation}>
										<TrashCanIcon />
									</Button>
								}
								<button
									className={clsx(
										'btn btn--primary',
										betslipStyle.betButton
									)}
									data-disabled={isFetching}
									onClick={onClick}
									ref={getRef('placebet-parlay-bet')}
								>
									<span className="btn__text">{t('lbl_bet')}</span>
									<span className="bet-slip__betting-amount">{getBettingAmount()}</span>
								</button>
							</div>
							<div className={clsx('bet-slip__footer', betslipStyle.footer)}>
								<BetSetting className={betslipStyle.galaxyBetSetting} />
								{!useSabaCoin ? (
									<div className={clsx(betslipStyle.textInfo, betslipStyle.textInfoMars)}>
										{t('lbl_AvailableFunds')}:
										<span className={betslipStyle.textSecondary}>{balance}</span>
									</div>
								) : null}
								<div className={clsx(betslipStyle.textInfo, betslipStyle.textInfoMars)}>
									{t('lbl_TotalBets')}:
									<span className={betslipStyle.textSecondary}>{getTotalBet(isIndependentMode)}</span>
								</div>
								<Payout payout={getTotalPayout(isIndependentMode)} isEndingGameMultiTicket={isEndingGameMultiTicket} />
								{/* <AcceptBetterOdds /> */}
							</div>
							<div className={betslipStyle.bottomFooter}>
								<BetSetting />
							</div>
						</div>
					) : null
				}
			</div>
		</>
	);
}